/**
 * The little formatting messages may carry, as many chat apps read it: **굵게**, ~~취소선~~, `코드` and code
 * blocks between ``` lines. Parsed into plain data that is rendered as React text nodes (never HTML), and
 * stripped where a message is read out or previewed. Nothing inside code is formatted, linked or mentioned.
 * `_` is not italics here: it would turn snake_case and file names into formatting.
 */
export type Inline = { kind: 'text'; text: string } | { kind: 'code'; text: string } | { kind: 'strong'; children: Inline[] } | { kind: 'strike'; children: Inline[] };
export type Block = { kind: 'lines'; lines: Inline[][] } | { kind: 'code'; text: string };

const FENCE = /^\s*```[\w+.#-]*\s*$/;
const CODE_SPAN = /`([^`\n]+)`/g;
// Opening and closing markers next to text, not spaces ("2 ** 3" stays as it is).
const EMPHASIS = /\*\*(?=\S)([\s\S]*?\S)\*\*|~~(?=\S)([\s\S]*?\S)~~/g;

export function parseMarkup(text: string): Block[] {
  const blocks: Block[] = [];
  const lines = text.split('\n');
  let plain: string[] = [];
  const flush = () => {
    if (plain.length) blocks.push({ kind: 'lines', lines: plain.map(parseInline) });
    plain = [];
  };
  for (let i = 0; i < lines.length; i += 1) {
    const close = FENCE.test(lines[i]!) ? lines.findIndex((line, j) => j > i && FENCE.test(line)) : -1;
    if (close < 0) {
      plain.push(lines[i]!);
      continue;
    }
    // A fence without its closing line is just text.
    flush();
    blocks.push({ kind: 'code', text: lines.slice(i + 1, close).join('\n') });
    i = close;
  }
  flush();
  return blocks;
}

function parseInline(line: string): Inline[] {
  const out: Inline[] = [];
  let at = 0;
  for (const match of line.matchAll(CODE_SPAN)) {
    out.push(...parseEmphasis(line.slice(at, match.index)));
    out.push({ kind: 'code', text: match[1]! });
    at = match.index + match[0].length;
  }
  out.push(...parseEmphasis(line.slice(at)));
  return out;
}

function parseEmphasis(text: string): Inline[] {
  const out: Inline[] = [];
  let at = 0;
  for (const match of text.matchAll(EMPHASIS)) {
    if (match.index > at) out.push({ kind: 'text', text: text.slice(at, match.index) });
    out.push(match[1] !== undefined ? { kind: 'strong', children: parseEmphasis(match[1]) } : { kind: 'strike', children: parseEmphasis(match[2]!) });
    at = match.index + match[0].length;
  }
  if (at < text.length) out.push({ kind: 'text', text: text.slice(at) });
  return out;
}

function inlineText(parts: readonly Inline[]): string {
  return parts.map((part) => (part.kind === 'strong' || part.kind === 'strike' ? inlineText(part.children) : part.text)).join('');
}

/** The text without its formatting marks, as a screen reader or a one-line preview should have it. */
export function plainText(text: string): string {
  return parseMarkup(text)
    .map((block) => (block.kind === 'code' ? block.text : block.lines.map(inlineText).join('\n')))
    .join('\n');
}
